import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoSwitch, RobotoTooltip } from "@/shared/components";
import {
  useUpdateScheduledTrigger,
  useUpdateTrigger,
} from "@/shared/domain/actions/hooks";
import {
  TriggerType,
  TriggerView,
} from "@/shared/domain/actions/TriggerRecord";
import { RobotoLimitExceededException } from "@/shared/http/exceptions";

interface TriggerSwitchProps {
  active: boolean;
  triggerId: string;
  triggerName: string;
  triggerType?: TriggerType;
  setErrorText: (arg: string) => void;
}

export const TriggerSwitch: React.FC<TriggerSwitchProps> = ({
  active,
  triggerId,
  triggerName,
  triggerType,
  setErrorText,
}) => {
  const { currentOrganization } = useAuth();

  const [switchState, setSwitchState] = React.useState<boolean>(active);

  const updateTriggerMutation = useUpdateTrigger();
  const updateScheduledTriggerMutation = useUpdateScheduledTrigger();

  const errorHandler = (error: Error) => {
    if (error.name === RobotoLimitExceededException.name) {
      setErrorText(
        "Your organization has reached the limit for active triggers. Please disable one of your existing triggers first, or upgrade your organization's account.",
      );
    } else {
      setErrorText(error.message);
    }
  };

  const updateRecord = () => {
    setErrorText("");

    if (triggerType === TriggerType.Scheduled) {
      updateScheduledTriggerMutation.mutate(
        {
          trigger_id: triggerId,
          updates: { enabled: !switchState },
        },
        {
          onError: errorHandler,
          onSuccess: (data: TriggerView) => {
            setSwitchState(data.enabled);
          },
        },
      );
    } else {
      updateTriggerMutation.mutate(
        {
          name: triggerName,
          resourceOwnerId: currentOrganization?.org_id || "",
          updates: { enabled: !switchState },
        },
        {
          onError: errorHandler,
          onSuccess: (data: TriggerView) => {
            setSwitchState(data.enabled);
          },
        },
      );
    }
  };

  const handleToggle = () => {
    void updateRecord();
  };

  return (
    <RobotoTooltip title={switchState ? "Disable Trigger" : "Enable Trigger"}>
      <RobotoSwitch
        checked={switchState}
        onChange={handleToggle}
        inputProps={{ "aria-label": "trigger toggle" }}
        color="success"
      />
    </RobotoTooltip>
  );
};
